import IImpact from '@models/IImpact';

export function getImpactIconName(
  impactLevel: string,
  style: 'rounded' | 'square' = 'rounded',
): string {
  switch (impactLevel) {
    case 'U.S. University':
      return style === 'rounded'
        ? 'emojione:flag-for-united-states'
        : 'emojione-v1:flag-for-united-states';
    case 'U.S. Entity':
      return style === 'rounded'
        ? 'emojione:flag-for-united-states'
        : 'emojione-v1:flag-for-united-states';
    case 'GB University':
      return style === 'rounded'
        ? 'emojione:flag-for-united-kingdom'
        : 'emojione-v1:flag-for-united-kingdom';
    case 'AU University':
      return style === 'rounded'
        ? 'emojione:flag-for-australia'
        : 'emojione-v1:flag-for-australia';
    case 'JP University':
      return style === 'rounded'
        ? 'emojione:flag-for-japan'
        : 'emojione-v1:flag-for-japan';
    case 'CA University':
      return style === 'rounded'
        ? 'emojione:flag-for-canada'
        : 'emojione-v1:flag-for-canada';
    case 'NZ University':
      return style === 'rounded'
        ? 'emojione:flag-for-new-zealand'
        : 'emojione-v1:flag-for-new-zealand';
    case 'SG University':
      return style === 'rounded'
        ? 'emojione:flag-for-singapore'
        : 'emojione-v1:flag-for-singapore';
    case 'TW University':
      return style === 'rounded'
        ? 'emojione:flag-for-taiwan'
        : 'emojione-v1:flag-for-taiwan';
    case 'IL University':
      return style === 'rounded'
        ? 'emojione:flag-for-israel'
        : 'emojione-v1:flag-for-israel';
    case 'NL University':
      return style === 'rounded'
        ? 'emojione:flag-for-netherlands'
        : 'emojione-v1:flag-for-netherlands';
    default:
      return 'ph:globe-hemisphere-east';
  }
}

export function getImpactHrefName(
  impactLevel: string,
  round?: boolean,
): string {
  switch (impactLevel) {
    case 'U.S. University':
      return `/svg/flags/flag-for-united-states${round ? '-round' : ''}.svg`;
    case 'U.S. Entity':
      return `/svg/flags/flag-for-united-states${round ? '-round' : ''}.svg`;
    case 'GB University':
      return `/svg/flags/flag-for-united-kingdom${round ? '-round' : ''}.svg`;
    case 'AU University':
      return `/svg/flags/flag-for-australia${round ? '-round' : ''}.svg`;
    case 'JP University':
      return `/svg/flags/flag-for-japan${round ? '-round' : ''}.svg`;
    case 'CA University':
      return `/svg/flags/flag-for-canada${round ? '-round' : ''}.svg`;
    case 'NZ University':
      return `/svg/flags/flag-for-new-zealand${round ? '-round' : ''}.svg`;
    case 'SG University':
      return `/svg/flags/flag-for-singapore${round ? '-round' : ''}.svg`;
    case 'TW University':
      return `/svg/flags/flag-for-taiwan${round ? '-round' : ''}.svg`;
    case 'NL University':
      return `/svg/flags/flag-for-netherlands${round ? '-round' : ''}.svg`;
    case 'IL University':
      return `/svg/flags/flag-for-israel${round ? '-round' : ''}.svg`;
    default:
      // Use non-round globe icon for unknown impact levels
      // The round version doesn't exist, so we ignore the 'round' parameter
      // for this fallback case to prevent 404 errors
      return `/svg/flags/globe-hemisphere-east.svg`;
  }
}

export type ImpactMeta = { level: string; icon: string }[] | null;

export function getImpactMeta(impacts: IImpact[]): ImpactMeta {
  if (!impacts || impacts.length === 0) {
    return null;
  }

  const counts: {
    'U.S. University'?: number;
    'U.S. Entity'?: number;
    'GB University'?: number;
    'AU University'?: number;
    'JP University'?: number;
    'CA University'?: number;
    'NZ University'?: number;
    'SG University'?: number;
    'TW University'?: number;
    'NL University'?: number;
    'IL University'?: number;
  } = {};

  for (const impact of impacts) {
    counts[impact.level] = counts[impact.level] ? counts[impact.level] + 1 : 1;
  }

  if (
    !(
      counts &&
      Object.keys(counts).length === 0 &&
      Object.getPrototypeOf(counts) === Object.prototype
    )
  ) {
    const arr = Object.values(counts);
    const max = Math.max(...arr);

    const level =
      Object.keys(counts)[
        Object.values(counts).findIndex((value) => value === max)
      ];

    if (level === 'U.S. University' || level === 'U.S. Entity') {
      return [
        {
          level,
          icon: getImpactIconName(level),
        },
        ...Object.keys(counts)
          .filter(
            (count) => count !== 'U.S. University' && count !== 'U.S. Entity',
          )
          .map((count) => ({ level: count, icon: getImpactIconName(count) })),
      ];
    }

    const tempMeta = [
      { level, icon: getImpactIconName(level) },
      ...Object.keys(counts)
        .filter((count) => count !== level)
        .map((count) => ({ level: count, icon: getImpactIconName(count) })),
    ];

    return Object.keys(counts).includes('U.S. University') &&
      Object.keys(counts).includes('U.S. Entity')
      ? tempMeta.filter((meta) => meta.level !== 'U.S. University')
      : tempMeta;
  }

  return null;
}
